import {createSelector} from 'reselect';
import {DateTime} from 'luxon';
import {Map as ImmutableMap, List as ImmutableList} from 'immutable';
import createCachedSelector, {LruCacheObject} from 're-reselect';
import {REDUCER_KEY} from './reducer';
import {getTransactionsByAccountId, TxnTypes} from '../transactions/selectors';
import {getGoalAccountIds} from "../accounts/selectors";
import {isGoalTxnInNonGoalAccount} from "../transactions/utils";


export const getGoalsStore = (state) => state[REDUCER_KEY];
export const getGoalsById = (state) => getGoalsStore(state).resourcesById;
export const getGoalsIsLoading = (state) => getGoalsStore(state).isLoading;
export const getLastSyncDate = (state: any) => getGoalsStore(state).lastSyncDate;


export const getGoalsSorted = createSelector(
  getGoalsById,
  (allGoals) => allGoals.sort((a, b) => {
    const dateA = DateTime.fromISO(a.modifiedAt);
    const dateB = DateTime.fromISO(b.modifiedAt);
    if (a.modifiedAt) return dateA.diff(dateB) > 0 ? -1 : 1;
    return -1;
  })
);
export const getCompletedGoalsById = createSelector(
  getGoalsSorted,
  (goalsById) => goalsById.filter((goal) => goal.completedOn && goal.completedOn !== '1970-01-01')
);

export const getActiveGoalsById = createSelector(
  getGoalsSorted,
  (goalsById) => goalsById.filter((goal) => !goal.completedOn || goal.completedOn === '1970-01-01')
);

// This selector will return all goal transactions matching id & month/year
export const getGoalTxnsForIdAndDate = createSelector(
  getTransactionsByAccountId,
  (state, props) => props.acctId,
  (state, props) => props.date,
  (allTxns, acctIdProp, dateProp) => {
    const goalTxns = allTxns?.get(acctIdProp);
    if (goalTxns?.size > 0) {
      return goalTxns.filter((txn) => {
        // filter goal txns by year & month (passed as property to selector)
        const date = DateTime.fromISO(txn.postedOn);
        return (date.hasSame(dateProp, 'month') && date.hasSame(dateProp, 'year'));
      })
    }
    return new ImmutableList();
  }
);


export const getUniqueContributors = createSelector(
  (state, props) => props.accountId,
  getTransactionsByAccountId,
  (acctId, allTxns) => {
    // Helper f'n
    const getBalance = (id) =>
      goalTxns
        .filter((txn) => txn.coa.id === id)
        .map((txn) => Number(txn.amount))
        .reduce((sum, current) => sum + current, 0);
    const balanceMap = new Map();
    const goalTxns = allTxns.get(acctId);
    if (goalTxns?.size > 0)
      goalTxns
        .map((txn) => txn.coa.id).toSet()
        .forEach((uid) => {
          const balance = getBalance(uid);
          if (balance > 0) balanceMap.set(uid, balance);
          else balanceMap.delete(uid);
        })
    return new ImmutableMap(balanceMap);
  }
);

export const getAccountsBreakdown = createSelector(
  (state) => getTransactionsByAccountId(state, {txnTypes: TxnTypes.GOALS}),
  getGoalAccountIds,
  (allTxns, goalAcctIds) => ImmutableMap().withMutations((mutableParent) => {
    allTxns.forEach((txnList, key) => {
      let childMap = ImmutableMap().withMutations((mutableChild) => {
        txnList.forEach((txn) => {
          if (isGoalTxnInNonGoalAccount(txn, goalAcctIds) && !(mutableChild.has(txn.coa?.id))) {
            mutableChild.set(txn.coa.id, Number(txn.amount))
          } else if (isGoalTxnInNonGoalAccount(txn, goalAcctIds)) {
            mutableChild.update(txn.coa?.id, (value) => value + Number(txn.amount))
          }

        });
      });
      childMap = childMap.filter((child) => Math.abs(child) > 0); // prune 0 values
      if (childMap.size > 0) {
        mutableParent.set(key, childMap);
      }
    })
  })
);


export const getGoalsByIds = createCachedSelector(
  getGoalsById,
  (state, ids) => ids,
  (goalsById, ids) => goalsById && goalsById.filter((goal) => ids && ids.includes(goal.id)),
)(
  (state, ids, selectorKey) => ids && ids.hashCode() + selectorKey,
  {cacheObject: new LruCacheObject({cacheSize: 4})},
);


